import { c as _c } from "react/compiler-runtime";
// Context of a task (Decision task-context): the records it is about as Chips (the label opens the record, ×
// detaches it, the note muted inside), outside links as Rows (open in a new tab), and Add: a picker over the
// snapshot's people, projects, tasks and repos (search field + rows, tap attaches). Single-word labels, POC look.
// Styles: book.css `.context`.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type ContextChange, type ContextItem, type Link } from "@/api";
import { errorText, useData } from "@/store";
import { Chip } from "./chips";
import { ErrorLine } from "./error-line";
import { Field } from "./field";
import { Initials } from "./initials";
import { LinkButton } from "./link-button";
import { Row } from "./row";
import { SectionHead } from "./section-head";
type Option = {
  id: string;
  kind: string;
  label: string;
};
const MAX_OPTIONS = 8;
type Props = {
  id: string;
  items: ContextItem[];
  links: Link[];
  onOpen: (id: string) => void;
  onChanged: () => void;
};
export function ContextSection(t0) {
  const $ = _c(48);
  const {
    id,
    items,
    links,
    onOpen,
    onChanged
  } = t0;
  const {
    t
  } = useTranslation();
  const snapshot = useData(_temp);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== id || $[1] !== onChanged) {
    t1 = async c => {
      ;
      try {
        await api.tasks.context(id, c);
        setError(null);
        onChanged();
      } catch (t2) {
        const e = t2;
        setError(errorText(e));
      }
    };
    $[0] = id;
    $[1] = onChanged;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const change = t1;
  let t2;
  if ($[3] !== id || $[4] !== items) {
    t2 = new Set([id, ...items.map(_temp2)]);
    $[3] = id;
    $[4] = items;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const attached = t2;
  let t3;
  if ($[6] !== attached || $[7] !== q || $[8] !== snapshot) {
    const all = snapshot ? [...snapshot.persons.map(_temp3), ...snapshot.projects.map(_temp4), ...snapshot.tasks.map(_temp5), ...snapshot.repos.map(_temp6)] : [];
    const needle = q.trim().toLowerCase();
    t3 = all.filter(o => !attached.has(o.id) && (!needle || o.label.toLowerCase().includes(needle))).slice(0, MAX_OPTIONS);
    $[6] = attached;
    $[7] = q;
    $[8] = snapshot;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const options = t3;
  let t4;
  if ($[10] !== t) {
    t4 = t("context.title");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== open) {
    t5 = () => (setOpen(!open), setQ(""));
    $[12] = open;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== open || $[15] !== t) {
    t6 = open ? t("context.done") : t("context.add");
    $[14] = open;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== open || $[18] !== t5 || $[19] !== t6) {
    t7 = <LinkButton data-t="context-add" aria-expanded={open} onClick={t5}>{t6}</LinkButton>;
    $[17] = open;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("context.title");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <SectionHead action={t7}>{t8}</SectionHead>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== change || $[27] !== items || $[28] !== onOpen || $[29] !== t) {
    t10 = items.length > 0 && <div className="chips" data-t="context-chips">{items.map(i_0 => <Chip key={i_0.id} t="context-chip" id={i_0.id} label={i_0.label} sub={i_0.note} removeLabel={t("context.remove")} onOpen={() => onOpen(i_0.id)} onRemove={() => void change({
        remove: [i_0.id]
      })} />)}</div>;
    $[26] = change;
    $[27] = items;
    $[28] = onOpen;
    $[29] = t;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== links) {
    t11 = links.length > 0 && <ul className="rows" data-t="context-links">{links.map(_temp7)}</ul>;
    $[31] = links;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== change || $[34] !== open || $[35] !== options || $[36] !== q || $[37] !== t) {
    t12 = open && <div className="picker" data-t="context-picker"><Field t="context-search" icon="search" type="search" enterKeyHint="search" placeholder={t("filter.search")} aria-label={t("filter.search")} autoFocus={true} value={q} onChange={e_0 => setQ(e_0.target.value)} /><ul className="rows">{options.map(o_0 => <li key={o_0.id}><Row t="context-option" id={o_0.id} lead={o_0.kind === "Person" ? <Initials name={o_0.label} /> : undefined} label={o_0.label} count={t(`context.kind.${o_0.kind}`)} onClick={() => void change({
            add: [o_0.id]
          })} /></li>)}</ul></div>;
    $[33] = change;
    $[34] = open;
    $[35] = options;
    $[36] = q;
    $[37] = t;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== error) {
    t13 = error && <ErrorLine>{error}</ErrorLine>;
    $[39] = error;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t4 || $[46] !== t9) {
    t14 = <section className="context" data-t="context" aria-label={t4}>{t9}{t10}{t11}{t12}{t13}</section>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t4;
    $[46] = t9;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
}
function _temp7(l) {
  return <li key={l.url}><Row t="context-link" label={l.title ?? l.url} count={l.kind} title={l.url} onClick={() => window.open(l.url, "_blank", "noopener")} /></li>;
}
function _temp6(r) {
  return {
    id: r.id,
    kind: "Repo",
    label: r.path
  };
}
function _temp5(x) {
  return {
    id: x.id,
    kind: "Task",
    label: x.title
  };
}
function _temp4(p_0) {
  return {
    id: p_0.id,
    kind: "Project",
    label: p_0.name
  };
}
function _temp3(p) {
  return {
    id: p.id,
    kind: "Person",
    label: p.name
  };
}
function _temp2(i) {
  return i.id;
}
function _temp(s) {
  return s.snapshot;
}